// 화면 권한 can() — 계약 PERMISSIONS 7 「화면 can()」·설계 2-6 켜는 순서.
// permMode 가 enforce 가 아니면 옛 규칙(accountPermission.permissions)으로 그리고, 새 규칙과 다르면 POST /perm/ui-diff 로 보고만.
// 이번 배포에서 사용자에게 보이는 메뉴는 바뀌지 않는다(enforce 는 서버 모드가 바뀐 날부터).
// admin-hospital·admin-manufacturer 같은 규칙 — 앱마다 다른 것은 PERM_APP 과 맨 아래 MENU_PERMS 표. 순수 모듈(tests/permUi.test.mjs).
export const PERM_APP = "admin-hospital";
const PERM_MODES = ["off", "log", "enforce"];

export const can = (perms, code) => Array.isArray(perms) && perms.includes(code);
export const canAny = (perms, codes = []) => codes.some((c) => can(perms, c));

/** 이 앱의 permMode: 문자열이면 그 값, {mode, perServer} 면 perServer[앱] ?? mode. 없거나 모르는 값이면 log(= 옛 규칙, 막지 않는 값) */
export const permModeOf = (modes, app = PERM_APP) => {
  let v = modes && modes.perm;
  if (v && typeof v === "object") v = (v.perServer && v.perServer[app]) || v.mode;
  return PERM_MODES.includes(v) ? v : "log";
};

/** 새 규칙으로 그리는 날인가(enforce + 응답에 perms 가 있음). 아니면 옛 규칙 */
export const shouldUseNewRule = (modes, perms, app = PERM_APP) =>
  permModeOf(modes, app) === "enforce" && Array.isArray(perms);

/** 새 규칙의 메뉴 보임(MENU_PERMS 의 코드 중 하나라도) */
export const menuVisibleNew = (perms, key, table = MENU_PERMS) =>
  !!table[key] && canAny(perms, table[key].codes);

/** 화면에 그릴 값: enforce 면 새 규칙, 아니면 옛 값 그대로 */
export const menuVisible = ({ key, legacy, perms, modes }, table = MENU_PERMS) =>
  shouldUseNewRule(modes, perms) ? menuVisibleNew(perms, key, table) : !!legacy;

/**
 * 옛 규칙과 새 규칙이 다른 메뉴 → POST /perm/ui-diff 본문 {app, screen, code, legacyAllowed, permAllowed} 목록.
 * perms 가 없으면(옛 inapi·W2 합류 전) 비교할 수 없어 [].
 * @param legacyByKey {메뉴 키: 옛 규칙으로 보이나}
 */
export const permUiDiffs = ({ legacyByKey, perms, screen = "sidenav", app = PERM_APP }, table = MENU_PERMS) => {
  if (!Array.isArray(perms)) return [];
  return Object.entries(legacyByKey || {})
    .filter(([key]) => table[key])
    .map(([key, legacy]) => ({
      app,
      screen: `${screen}:${key}`,
      code: table[key].codes[0],
      legacyAllowed: !!legacy,
      permAllowed: menuVisibleNew(perms, key, table),
    }))
    .filter((d) => d.legacyAllowed !== d.permAllowed);
};

/** 계정·코드마다 한 번만 보내려고 localStorage 에 남기는 표시 키(소음 줄이기라 저장소가 비어도 괜찮음) */
export const diffSeenKey = (accountId, code) => `certi.permUiDiff.${accountId || "-"}.${code}`;

// ── 앱마다 다른 표 ─────────────────────────────────────────────
// 메뉴 키(store accountPermission.permissions 의 키) → 그 메뉴를 보이게 하는 view 코드(설계 perm.md 2-3, 계약 permission-catalog.v1.json 에 있는 코드만).
// route = 그 메뉴가 여는 라우트 이름(RouterEnum, 시험이 실제 라우트에 있는지 봄).
// 「상담/챗봇」(counselingChatbotManagement)은 코드가 없어져(perm.md 2-3 「없앰」) 표에 넣지 않음 — studio 코드가 하나라도 있으면 진입.
export const MENU_PERMS = {
  hospitalSettings: { route: "BrandSetting", codes: ["settings.brand.view"] },
  dentalCert: { route: "DentIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  ophCert: { route: "OphIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  plasticCert: { route: "PsIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  dermaCert: { route: "DermIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  internalCert: { route: "ImIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  orthoCert: { route: "OsIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  vetCert: { route: "VetIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  generalCert: { route: "CommonIssueCertificate", codes: ["cert.issue.send", "cert.template.view"] },
  // 회사 id 로 예약 메뉴를 숨기는 곳(SidenavList onlyShowReservationMenuTestBedHospital)도 옛 값에 같이 넣어 비교만(perm.md 3-3)
  reservationManagement: { route: "ReservationList", codes: ["reservation.booking.view", "review.post.view"] },
  issuancePatientManagement: { route: "CertificateList", codes: ["cert.issue.view", "patient.record.view"] },
  paymentHistory: { route: "Subscription", codes: ["billing.payment.view"] },
  orderManagement: { route: "order-container", codes: ["commerce.order.view"] },
  electronicContract: { route: "ContractList", codes: ["contract.sales.view"] },
  treatmentConsent: { route: "ConsentContract", codes: ["contract.consent.view"] },
  laborContract: { route: "LaborContract", codes: ["contract.labor.view"] },
  hospitalInfo: { route: "HospitalInfo", codes: ["settings.company.view"] },
  accountManagement: { route: "SubAccountList", codes: ["account.staff.view"] },
  alimtalkManagement: { route: "AlimtalkChannelSettings", codes: ["settings.channel.view", "message.template.view"] },
};

/** 배경 요청을 보내도 되나: 새 규칙 날엔 그 코드가 있을 때만, 그 전엔 늘(거부가 이용 내역에 남아 숨긴 메뉴 확인은 보내지 않음, 10-30 I10) */
export const callAllowed = (modes, perms, codes = []) => !shouldUseNewRule(modes, perms) || canAny(perms, codes);

// 한 묶음 안의 항목 → 그 항목 view 코드. 묶음은 MENU_PERMS(옛 키)로, 항목은 여기로 하나씩 숨김
// (10-30 I10: 「발행 내역」만 줘도 「환자 관리」, 「예약 관리」만 줘도 「후기 관리」가 보였음)
export const SUB_MENU_PERMS = {
  certificateList: ["cert.issue.view"],
  customerList: ["patient.record.view"],
  reservationList: ["reservation.booking.view"],
  reviewManagement: ["review.post.view"],
  studioChatbot: ["chatbot.bot.view"],
  studioCounsel: ["counsel.room.view"],
  voicebot: ["voicebot.call.view"],
};

export const subMenuVisible = (modes, perms, key, table = SUB_MENU_PERMS) => callAllowed(modes, perms, table[key]);

/** 사이드바 메뉴 키 하나가 보이나 — 사이드바(SidenavList permissions)·라우터가 같이 씀. 표에 없는 키(상담/챗봇)는 옛 값 그대로 */
export const menuAllowed = (key, { legacy, perms, modes }, table = MENU_PERMS) =>
  table[key] ? menuVisible({ key, legacy: legacy?.[key], perms, modes }, table) : !!legacy?.[key];

// 주소 앞머리(경로 마디 단위) → 그 화면을 여는 사이드바 메뉴(key = accountPermission 키, sub = SUB_MENU_PERMS 키).
// 하위 화면(등록·수정·상세·작성)은 앞머리로 부모 메뉴를 따름. 표에 없는 화면(대시보드·공지·상품 구매·모바일 등)은 막지 않음
export const ROUTE_MENUS = {
  "/brand-setting": { key: "hospitalSettings" },
  "/share-links": { key: "hospitalSettings" }, // 브랜드 설정 머리(BrandHeader)의 버튼으로만 진입
  "/dent": { key: "dentalCert" },
  "/oph": { key: "ophCert" },
  "/ps": { key: "plasticCert" },
  "/derm": { key: "dermaCert" },
  "/internal": { key: "internalCert" },
  "/os": { key: "orthoCert" },
  "/vet": { key: "vetCert" },
  "/common": { key: "generalCert" },
  "/reservation-list": { key: "reservationManagement", sub: "reservationList" },
  "/review": { key: "reservationManagement", sub: "reviewManagement" },
  "/certificate-list": { key: "issuancePatientManagement", sub: "certificateList" },
  "/customer-list": { key: "issuancePatientManagement", sub: "customerList" },
  "/customer-details": { key: "issuancePatientManagement", sub: "customerList" },
  "/subscription": { key: "paymentHistory" },
  "/order-container": { key: "orderManagement" },
  "/contract": { key: "electronicContract" },
  "/electronic-sign/consent": { key: "treatmentConsent" },
  "/electronic-sign/labor": { key: "laborContract" },
  "/info": { key: "hospitalInfo" },
  "/info-verify": { key: "hospitalInfo" },
  "/info-edit": { key: "hospitalInfo" },
  "/account-management": { key: "accountManagement" },
  "/alimtalk": { key: "alimtalkManagement" },
};

/** 옛 규칙(직원 메뉴 권한)으로 막을 때 안내(새 권한 코드가 없어 「필요: 이름」을 못 씀) */
export const MENU_DENIED_TEXT = "이 메뉴를 볼 권한이 없습니다. 첫 화면으로 이동했어요.";

/**
 * 주소로 연 화면이 사이드바에 안 보이면 거절 {codes}, 보이면 null — 사이드바와 같은 menuAllowed·subMenuVisible.
 * legacy = 회사 정보의 accountPermission.permissions, 못 받았으면 null → 옛 규칙으로는 막지 않음.
 * codes: 새 규칙(enforce)으로 막으면 그 화면 코드(안내 「필요: 이름」), 옛 규칙이면 null(MENU_DENIED_TEXT)
 */
export const routeDenial = ({ path, legacy, perms, modes, locale }, table = ROUTE_MENUS) => {
  const p = String(path || "");
  const prefix = Object.keys(table).find((x) => p === x || p.startsWith(`${x}/`));
  if (!prefix) return null;
  const { key, sub } = table[prefix];
  // 일본어 사이드바는 치과 메뉴를 권한 조건 없이 그림(SidenavList languageCode 'ja') → 주소도 막지 않음
  if (locale === "ja" && key === "dentalCert") return null;
  const newRule = shouldUseNewRule(modes, perms);
  if (!newRule && !legacy) return null;
  if (menuAllowed(key, { legacy, perms, modes }) && (!sub || subMenuVisible(modes, perms, sub))) return null;
  return { codes: newRule ? (sub ? SUB_MENU_PERMS[sub] : MENU_PERMS[key].codes) : null };
};
